import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ScanLine, Users, CreditCard, Shield, Sparkles, Calculator, User, Brain, UserPlus, Zap, Stars, Gem, CircleDollarSign, UserSquare2, Receipt, BanknoteIcon } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { useMediaQuery } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
const fadeIn = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  transition: {
    duration: 0.3
  }
};
const LandingPage = () => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const {
    t,
    i18n
  } = useTranslation();
  const isJapanese = i18n.language === 'ja';
  const handleGetStarted = () => {
    navigate("/signup");
  };
  return <div className="min-h-screen bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 relative">
            <div className="flex-1 flex items-center">
              <img src="https://tkrrgpbntaqiupgcayjm.supabase.co/storage/v1/object/public/Logo//SakuWari_logo_transparent_black.png" alt="SakuWari Logo" className="h-7 dark:invert" />
            </div>
            
            <div className="flex-1 flex justify-end">
              <Button onClick={() => navigate("/login")} variant="ghost" className="text-sm hover:bg-purple/10">
                {isJapanese ? "ログイン" : "Login"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <section className="pt-24 pb-16">
        <div className="container mx-auto px-4 text-center space-y-8">
          <h1 className="text-4xl md:text-6xl font-bold text-foreground">
            {isJapanese ? <span className="bg-gradient-to-r from-purple to-green bg-clip-text text-transparent inline-block w-full mx-[17px]">
                サクッと精算、<br />思い出に没頭。
              </span> : <>
                <span className="relative inline-block">
                  Smart
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-purple to-green"></span>
                </span>
                {" bill splitting for"}<br className="md:hidden" />
                {"enjoyable living"}
              </>}
          </h1>

          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
            {isJapanese ? <>
                  <span className="font-medium bg-gradient-to-r from-purple to-green bg-clip-text text-transparent mx-[5px]">
                    AI
                  </span>
                  <span className="mx-px px-0">で実現する、これからの割り勘。</span>
                </> : "Keep payments with friends simple."}
            <br className="md:hidden" />
            {!isJapanese && <>
                <span className="font-medium bg-gradient-to-r from-purple to-green bg-clip-text text-transparent">
                  {isJapanese ? "AI駆動" : "AI-powered"}
                </span>
                {isJapanese ? "の計算で時間を節約。" : " calculations save you time."}
              </>}
          </p>

          <div>
            <Button onClick={handleGetStarted} className="text-lg py-6 px-8 rounded-xl shadow-lg bg-gradient-to-r from-purple to-green hover:from-purple/90 hover:to-green/90">
              {isJapanese ? "今すぐ始める" : "Get Started Now"}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          <div className="mt-12 max-w-4xl mx-auto">
            <div className="relative p-4 rounded-2xl bg-card shadow-xl border border-border">
              <div className="absolute -top-3 -right-3 bg-gradient-to-r from-purple to-green text-primary-foreground text-xs px-3 py-1 rounded-full font-medium">
                {isJapanese ? "AI搭載" : "AI-Powered"}
              </div>
              <div className="aspect-video w-full rounded-xl bg-gradient-to-br from-purple/5 to-green/20 shadow-inner flex items-center justify-center p-0">
                <img src="https://tkrrgpbntaqiupgcayjm.supabase.co/storage/v1/object/sign/Images/LP%20phone.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJJbWFnZXMvTFAgcGhvbmUucG5nIiwiaWF0IjoxNzQxNTEwMjU3LCJleHAiOjQ4OTUxMTAyNTd9.fVVo8d1ZnkyD30OgO-xoXYfJ-krU1BAioeMJDdAMxQc" alt="SakuWari App Preview" className="w-full h-full object-cover rounded-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {isJapanese && <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-purple/10 to-background opacity-70"></div>
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple to-transparent"></div>
          
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-purple/20 to-purple/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-green/20 to-purple/10 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center space-y-10">
              <div className="inline-flex items-center justify-center mb-4">
                <div className="h-px w-12 bg-gradient-to-r from-purple to-green"></div>
                <span className="mx-3 text-transparent bg-gradient-to-r from-purple to-green bg-clip-text font-bold tracking-wider text-lg">CONCEPT</span>
                <div className="h-px w-12 bg-gradient-to-r from-green to-purple"></div>
              </div>
              
              <div className="relative p-8 md:p-10 rounded-2xl overflow-hidden border border-purple/20 shadow-[0_10px_40px_-15px_rgba(139,92,246,0.3)]">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.07] to-white/[0.03] dark:from-black/10 dark:to-black/5 backdrop-blur-sm"></div>
                
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-gradient-to-br from-purple/30 to-green/30 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-purple/30 to-green/30 rounded-full blur-3xl animate-pulse" style={{
              animationDelay: "1s"
            }}></div>
                
                <div className="absolute inset-0 overflow-hidden opacity-10">
                  <div className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-purple to-transparent top-0 animate-scan"></div>
                </div>
                
                <div className="relative z-10">
                  <div className="flex justify-center mb-8">
                    <Sparkles className="h-10 w-10 text-purple animate-pulse" />
                  </div>
                  
                  <div className="text-sm md:text-lg font-medium leading-relaxed tracking-wide text-center">
                    <p className="mb-5 text-foreground">
                      友人とのかけがえのない時間を、<br />
                      清算の手間で台無しにしない。
                    </p>
                    <p className="mb-5">
                      <span className="font-bold text-transparent bg-gradient-to-r from-purple to-green bg-clip-text">SakuWari</span>
                      <span className="text-foreground">は、最新の</span>
                      <span className="font-bold text-transparent bg-gradient-to-r from-purple to-green bg-clip-text">AI技術</span>
                      <span className="text-foreground">を活用し、<br />
                      割り勘のわずらわしさを極限まで削減。</span>
                    </p>
                    <p className="text-foreground text-center px-[41px]">お金の整理に気を取られず、
大切な思い出をそのままに。</p>
                  </div>
                </div>
                
                <div className="absolute top-6 left-6 w-3 h-3 rounded-full bg-gradient-to-r from-purple to-green"></div>
                <div className="absolute bottom-6 right-6 w-3 h-3 rounded-full bg-gradient-to-r from-green to-purple"></div>
                <div className="absolute top-6 right-6 w-2 h-2 rounded-full bg-green"></div>
                <div className="absolute bottom-6 left-6 w-2 h-2 rounded-full bg-purple"></div>
              </div>
              
              <div className="flex justify-center">
                
              </div>
            </div>
          </div>
        </section>}

      {isJapanese && <div className="container mx-auto px-4 pb-8 text-center">
        <Button onClick={handleGetStarted} className="text-lg py-6 px-8 rounded-xl shadow-lg bg-gradient-to-r from-purple to-green hover:from-purple/90 hover:to-green/90">
          {isJapanese ? "今すぐ始める" : "Get Started Now"}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>}

      {isJapanese && <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-green/5 to-background opacity-70"></div>
          
          
          <div className="absolute top-40 right-10 w-40 h-40 bg-gradient-to-br from-purple/10 to-green/10 rounded-full blur-3xl animate-pulse-glow"></div>
          <div className="absolute bottom-40 left-10 w-40 h-40 bg-gradient-to-br from-green/10 to-purple/10 rounded-full blur-3xl animate-pulse-glow" style={{
        animationDelay: "1.5s"
      }}></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto space-y-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center mb-4">
                  <div className="h-px w-12 bg-gradient-to-r from-purple to-green"></div>
                  <span className="mx-3 text-transparent bg-gradient-to-r from-purple to-green bg-clip-text font-bold tracking-wider text-lg">FEATURES</span>
                  <div className="h-px w-12 bg-gradient-to-r from-green to-purple"></div>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-center bg-gradient-to-r from-purple via-magenta to-green bg-clip-text text-transparent mb-2">
                  3つのサクッと
                </h2>
                <div className="flex justify-center mb-10">
                  
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="group fancy-card rounded-xl p-8 shadow-lg hover:shadow-purple/10 transition-all duration-500 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple/5 to-green/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute -right-20 -top-20 w-40 h-40 bg-gradient-to-br from-purple/10 to-green/10 rounded-full blur-3xl opacity-70 group-hover:opacity-100 transition-all duration-700 animate-float"></div>
                  
                  <div className="absolute right-6 top-6 z-10">
                    <div className="p-4 rounded-full bg-gradient-to-r from-purple/20 to-green/20 shadow-inner border border-purple/10 group-hover:from-purple/30 group-hover:to-green/30 transition-all duration-300 animate-pulse-glow">
                      <Brain className="h-8 w-8 text-purple" />
                    </div>
                  </div>
                  
                  <div className="max-w-[70%] relative z-10">
                    <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple to-green bg-clip-text text-transparent">AIで記録をサクッと</h3>
                    <div className="space-y-3 text-muted-foreground">
                      <p className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full bg-purple mr-2"></span>
                        レシートを撮るだけで、AIが自動入力＆計算
                      </p>
                      <p className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full bg-green mr-2"></span>
                        金額の入力も、計算の手間もゼロ
                      </p>
                      <p className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-purple to-green mr-2"></span>
                        面倒な作業から解放され、すぐに記録完了
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="group fancy-card rounded-xl p-8 shadow-lg hover:shadow-purple/10 transition-all duration-500 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple/5 to-green/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute -right-20 -top-20 w-40 h-40 bg-gradient-to-br from-purple/10 to-green/10 rounded-full blur-3xl opacity-70 group-hover:opacity-100 transition-all duration-700 animate-float" style={{
                animationDelay: "0.5s"
              }}></div>
                  
                  <div className="absolute right-6 top-6 z-10">
                    <div className="p-4 rounded-full bg-gradient-to-r from-purple/20 to-green/20 shadow-inner border border-purple/10 group-hover:from-purple/30 group-hover:to-green/30 transition-all duration-300 animate-pulse-glow" style={{
                  animationDelay: "0.3s"
                }}>
                      <UserPlus className="h-8 w-8 text-purple" />
                    </div>
                  </div>
                  
                  <div className="max-w-[70%] relative z-10">
                    <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple to-green bg-clip-text text-transparent">友人登録をサクッと</h3>
                    <div className="space-y-3 text-muted-foreground">
                      <p className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full bg-purple mr-2"></span>
                        一度登録すれば、次回からすぐに追加
                      </p>
                      <p className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full bg-green mr-2"></span>
                        旅行や飲み会のたびに手入力する必要なし
                      </p>
                      <p className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-purple to-green mr-2"></span>
                        グループ作成も驚くほどスムーズに
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="group fancy-card rounded-xl p-8 shadow-lg hover:shadow-purple/10 transition-all duration-500 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple/5 to-green/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute -right-20 -top-20 w-40 h-40 bg-gradient-to-br from-purple/10 to-green/10 rounded-full blur-3xl opacity-70 group-hover:opacity-100 transition-all duration-700 animate-float" style={{
                animationDelay: "1s"
              }}></div>
                  
                  <div className="absolute right-6 top-6 z-10">
                    <div className="p-4 rounded-full bg-gradient-to-r from-purple/20 to-green/20 shadow-inner border border-purple/10 group-hover:from-purple/30 group-hover:to-green/30 transition-all duration-300 animate-pulse-glow" style={{
                  animationDelay: "0.7s"
                }}>
                      <CircleDollarSign className="h-8 w-8 text-purple" />
                    </div>
                  </div>
                  
                  <div className="max-w-[70%] relative z-10">
                    <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple to-green bg-clip-text text-transparent">支払いをサクッと</h3>
                    <div className="space-y-3 text-muted-foreground">
                      <p className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full bg-purple mr-2"></span>
                        銀行口座やPayPayと連携し、その場で完結
                      </p>
                      <p className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full bg-green mr-2"></span>
                        支払い情報を毎回共有する手間なし
                      </p>
                      <p className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-purple to-green mr-2"></span>
                        サービス内でそのままスムーズに送金可能
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>}
        
      {isJapanese && <div className="container mx-auto px-4 pb-8 text-center">
        <Button onClick={handleGetStarted} className="text-lg py-6 px-8 rounded-xl shadow-lg bg-gradient-to-r from-purple to-green hover:from-purple/90 hover:to-green/90">
          {isJapanese ? "今すぐ始める" : "Get Started Now"}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>}

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center">
                  <div className="h-px w-8 bg-purple"></div>
                  <span className="mx-3 text-purple text-sm font-semibold">
                    {isJapanese ? "使い方" : "HOW IT WORKS"}
                  </span>
                </div>
                <h3 className="text-3xl font-bold text-foreground">
                  {isJapanese ? "サクッと始めて、" : "Three simple steps to"}
                  <br />
                  <span className="bg-gradient-to-r from-purple to-green bg-clip-text text-transparent">
                    {isJapanese ? "サクッと清算" : "split bills"}
                  </span>
                  {isJapanese ? "" : ""}
                </h3>
                <div className="space-y-6">
                  {[{
                  num: "1",
                  title: isJapanese ? "グループを作成" : "Create a group",
                  desc: isJapanese ? "友達を招待し、割り勘グループを作成" : "Invite your friends and create a group.",
                  icon: <Users className="h-5 w-5 text-purple" />
                }, {
                  num: "2",
                  title: isJapanese ? "支払いを記録" : "Record payments",
                  desc: isJapanese ? "支払った金額を入力するか、AIスキャンで一瞬登録" : "Enter payment amounts or scan receipts.",
                  icon: <Receipt className="h-5 w-5 text-purple" />
                }, {
                  num: "3",
                  title: isJapanese ? "自動計算で清算" : "Automatic calculation",
                  desc: isJapanese ? "AIが誰がいくら払うべきかを瞬時に計算" : "Automatically calculate who should pay what. Pay directly to your friends via PayPay or bank transfer.",
                  icon: <Calculator className="h-5 w-5 text-purple" />
                }, {
                  num: "4",
                  title: isJapanese ? "サービス内で支払い完了" : "Direct payments",
                  desc: isJapanese ? "振込先情報をもとに、スムーズに支払い完了" : "Pay directly via bank or apps",
                  icon: <BanknoteIcon className="h-5 w-5 text-purple" />
                }].map(step => <div key={step.num} className="flex items-start gap-4 group">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-purple/20 to-green/20 flex items-center justify-center relative group-hover:from-purple/30 group-hover:to-green/30 transition-colors">
                        <span className="bg-gradient-to-r from-purple to-green bg-clip-text text-transparent font-bold">{step.num}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          {step.icon}
                          <h4 className="text-lg font-semibold">{step.title}</h4>
                        </div>
                        <p className="text-muted-foreground">{step.desc}</p>
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="relative flex justify-center">
                
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-muted/30 to-background relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple to-transparent"></div>
        
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <div className="inline-block">
              <div className="bg-gradient-to-r from-purple/20 to-green/20 p-4 rounded-full">
                <Shield className="h-12 w-12 text-purple" />
              </div>
            </div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-purple to-green bg-clip-text text-transparent">
              {isJapanese ? "安心・安全な取引" : "Secure Transactions"}
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {isJapanese ? "データは暗号化され、安全に保管されます。あなたの情報は常に保護されています。" : "Your data is encrypted and securely stored. Your information is always protected."}
            </p>
            <div className="pt-8">
              <Button onClick={handleGetStarted} variant="outline" className="text-lg py-6 px-8 rounded-xl border border-purple hover:bg-gradient-to-r hover:from-purple/5 hover:to-green/5 transition-all duration-300">
                {isJapanese ? "無料でアカウント作成" : "Create free account"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple to-transparent"></div>
        <div className="container mx-auto px-4 py-12">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center mb-4">
              <img src="https://tkrrgpbntaqiupgcayjm.supabase.co/storage/v1/object/public/Logo//SakuWari_logo_transparent_black.png" alt="SakuWari Logo" className="h-7 dark:invert" />
            </div>
            <p className="text-sm text-muted-foreground">© 2024 Sakuwari. All rights reserved.</p>
            <p className="text-sm text-muted-foreground">
              {isJapanese ? "安全・簡単・スマートな割り勘アプリ" : "Safe, easy, and smart bill splitting app"}
            </p>
          </div>
        </div>
      </footer>
    </div>;
};
const FeatureCard = ({
  icon,
  title,
  description,
  isHighlighted = false
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  isHighlighted?: boolean;
}) => {
  return;
};
export default LandingPage;